/**
 * 节点详情卡片组件（方案A：已完成订单详情展示）。
 *
 * 用于只读模式下展示当前节点的详细信息：
 * - 节点基本信息（名称、状态、操作人、时间）
 * - 节点附件（照片/文件）
 * - 节点备注
 *
 * 对接后端 API：
 * - GET /container/node/list?containerId=orderId  获取节点记录（container_id 实际存储 orderId）
 * - GET /container/node/documents?nodeRecordId=xxx  获取节点单据
 * - GET /file/{fileId}/url  获取文件预览URL
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import NotesIcon from '@mui/icons-material/Notes';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { getNodeRecordsByOrder, getNodeDocuments, getFileUrl } from '@/api/container';
import type { NodeRecordItem, NodeDocumentItem } from '@/api/container';
import { useNodeConfig } from '@/utils/nodeConfig';
import { StatusPill, cardSx } from './ui';
import { T } from '@/theme/tokens';

interface NodeDetailCardProps {
  orderId: string;
  nodeCode: string;
}

interface FileWithPreview extends NodeDocumentItem {
  previewUrl?: string;
  isImage?: boolean;
}

export default function NodeDetailCard({ orderId, nodeCode }: NodeDetailCardProps) {
  const { getNodeName } = useNodeConfig();
  const [loading, setLoading] = useState(true);
  const [nodeRecord, setNodeRecord] = useState<NodeRecordItem | null>(null);
  const [documents, setDocuments] = useState<FileWithPreview[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<FileWithPreview | null>(null);

  const loadData = useCallback(async () => {
    if (!orderId || !nodeCode) return;
    setLoading(true);
    try {
      // 获取节点记录（container_id 实际存储 orderId）
      const records = await getNodeRecordsByOrder(orderId);
      const recordList = Array.isArray(records) ? records : [];
      // 找到当前节点的记录
      const currentRecord = recordList.find(r => r.nodeCode === nodeCode);
      if (currentRecord) {
        setNodeRecord(currentRecord);
        // 获取节点单据
        const docs = await getNodeDocuments(currentRecord.id);
        const docList = Array.isArray(docs) ? docs : [];
        // 加载文件预览URL
        const docsWithPreview: FileWithPreview[] = await Promise.all(
          docList.map(async (doc) => {
            let previewUrl: string | undefined;
            let isImage = false;
            if (doc.fileId) {
              try {
                previewUrl = await getFileUrl(doc.fileId);
                isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(doc.fileName || '');
              } catch {
                // 忽略错误
              }
            }
            return { ...doc, previewUrl, isImage };
          })
        );
        setDocuments(docsWithPreview);
      }
    } catch (err) {
      console.error('加载节点详情失败', err);
    } finally {
      setLoading(false);
    }
  }, [orderId, nodeCode]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFileClick = (file: FileWithPreview) => {
    setPreviewFile(file);
    setPreviewOpen(true);
  };

  const handleDownload = async () => {
    if (previewFile?.previewUrl) {
      try {
        const response = await fetch(previewFile.previewUrl);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = previewFile.fileName || 'file';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch {
        window.open(previewFile.previewUrl, '_blank');
      }
    }
  };

  // 解析照片 JSON
  const photos: string[] = (() => {
    if (!nodeRecord?.photos) return [];
    try {
      const parsed = JSON.parse(nodeRecord.photos);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  })();

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress size={24} />
        <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 1 }}>
          加载节点详情...
        </Typography>
      </Box>
    );
  }

  if (!nodeRecord) {
    return (
      <Box sx={{ ...cardSx, p: 2.5, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '13px', color: T.ink3 }}>
          暂无节点记录
        </Typography>
      </Box>
    );
  }

  const kvRowSx = {
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    fontSize: '13px',
    color: T.ink2,
    py: '9px',
    '& + &': { borderTop: `1px solid ${T.line}` },
    '& .MuiSvgIcon-root': { fontSize: 16, color: T.ink3 },
  } as const;
  const subLabelSx = { display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '12px', fontWeight: 600, color: T.ink3, mt: 1.5, mb: '7px' } as const;

  return (
    <Box sx={{ ...cardSx, p: 2 }}>
      {/* 标题 */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', mb: 1.5 }}>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            bgcolor: T.greenSoft,
            color: T.green,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <CheckRoundedIcon sx={{ fontSize: 16 }} />
        </Box>
        <Typography sx={{ fontSize: '15px', fontWeight: 700, color: T.ink }}>节点详情</Typography>
        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: T.blue, ml: 'auto' }} noWrap>
          {getNodeName(nodeCode)}
        </Typography>
        <StatusPill tone="green">已完成</StatusPill>
      </Box>

      {/* 节点基本信息 */}
      {(nodeRecord.operatorName || nodeRecord.occurredAt) && (
        <Box>
          {nodeRecord.operatorName && (
            <Box sx={kvRowSx}>
              <PersonOutlineIcon />
              <span>
                操作人 · <Box component="b" sx={{ color: T.ink, fontWeight: 600 }}>{nodeRecord.operatorName}</Box>
              </span>
            </Box>
          )}
          {nodeRecord.occurredAt && (
            <Box sx={kvRowSx}>
              <AccessTimeIcon />
              <span>
                完成时间 ·{' '}
                <Box component="b" sx={{ color: T.ink, fontWeight: 600, fontFamily: T.mono, fontSize: '12.5px' }}>
                  {new Date(nodeRecord.occurredAt).toLocaleString('zh-CN')}
                </Box>
              </span>
            </Box>
          )}
        </Box>
      )}

      {/* 备注 */}
      {nodeRecord.notes && (
        <Box>
          <Box sx={subLabelSx}>
            <NotesIcon sx={{ fontSize: 14 }} />
            备注
          </Box>
          <Box
            sx={{
              px: '13px',
              py: '11px',
              bgcolor: T.line2,
              border: `1px solid ${T.line}`,
              borderRadius: T.rSm,
              fontSize: '13px',
              color: T.ink2,
              lineHeight: 1.6,
            }}
          >
            {nodeRecord.notes}
          </Box>
        </Box>
      )}

      {/* 照片 */}
      {photos.length > 0 && (
        <Box>
          <Box sx={subLabelSx}>
            <ImageIcon sx={{ fontSize: 14 }} />
            照片 ({photos.length})
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {photos.map((photo, idx) => (
              <Box
                key={idx}
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: T.rSm,
                  overflow: 'hidden',
                  border: `1px solid ${T.line}`,
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setPreviewFile({
                    id: `photo-${idx}`,
                    fileName: `照片 ${idx + 1}`,
                    previewUrl: photo,
                    isImage: true,
                  });
                  setPreviewOpen(true);
                }}
              >
                <img
                  src={photo}
                  alt={`照片 ${idx + 1}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* 附件/单据 */}
      {documents.length > 0 && (
        <Box>
          <Box sx={subLabelSx}>
            <DescriptionIcon sx={{ fontSize: 14 }} />
            附件 ({documents.length})
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {documents.map((doc) => (
              <Box
                key={doc.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1,
                  bgcolor: T.line2,
                  borderRadius: T.rSm,
                  border: `1px solid ${T.line}`,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: T.brandSoft },
                }}
                onClick={() => handleFileClick(doc)}
              >
                {doc.isImage && doc.previewUrl ? (
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '4px',
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <img
                      src={doc.previewUrl}
                      alt={doc.fileName || '附件'}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '4px',
                      bgcolor: T.amberSoft,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                    }}
                  >
                    📄
                  </Box>
                )}
                <Typography sx={{ fontSize: '12px', color: 'primary.main', flex: 1 }}>
                  {doc.fileName || '附件'}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* 预览弹窗 */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          component="div"
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {previewFile?.fileName || '预览'}
          </Typography>
          <IconButton onClick={() => setPreviewOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, minHeight: 300 }}
        >
          {previewFile?.isImage && previewFile.previewUrl ? (
            <img
              src={previewFile.previewUrl}
              alt={previewFile.fileName}
              style={{ maxWidth: '100%', maxHeight: 500, objectFit: 'contain', borderRadius: 4 }}
            />
          ) : previewFile?.previewUrl && previewFile.fileName?.match(/\.pdf$/i) ? (
            <iframe
              src={previewFile.previewUrl}
              title={previewFile.fileName}
              style={{ width: '100%', height: 500, border: '1px solid #E0E0E0', borderRadius: 4 }}
            />
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ fontSize: '48px', mb: 2 }}>📄</Typography>
              <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
                {previewFile?.fileName}
              </Typography>
            </Box>
          )}
          {previewFile?.previewUrl && (
            <Button
              variant="contained"
              onClick={handleDownload}
              sx={{ mt: 2, textTransform: 'none' }}
            >
              下载文件
            </Button>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
